// Файл AccessContext.js

/**
 * Контекст загружает права пользователя
 * после авторизации.
 * Он получает общие настройки и применяет
 * персональную тему.
 * Контекст проверяет разрешения при переходах
 * между конфигурациями.
 */

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    install_navigation_settings,
} from '../components/TabWidget/navigation_settings_store';
import { apply_app_theme } from '../shared_code/app_themes/app_theme';
import {
    DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS,
    install_interface_customization_settings,
} from '../shared_code/interface_customization/interface_customization_store';
import {
    load_interface_customization_profile,
} from './access_context_parts/interface_profile_request';
import {
    build_access_map,
    build_access_settings,
    clear_cached_access,
    fetch_access_rights,
    has_recent_authorization,
    read_cached_rights,
    request_with_retry,
    save_cached_rights,
} from './access_context_parts/access_helpers';
import { apiService } from './api';
import {
    subscribe_access_realtime,
} from './access_context_parts/access_realtime_sync';



const login_access_cache_ms = 30 * 1000;


const AccessContext = createContext({
    hasAccess: () => true,
    filterTabs: (tabs) => tabs,
    get_access_setting: () => null,
    refresh_access: async () => [],
    loaded: false,
    is_authorized: null,
});

const load_personalization_settings = () => (
    load_interface_customization_profile(apiService)
);

export const useAccess = () => useContext(AccessContext);

export const AccessProvider = ({ children }) => {
    const [initial_access] = useState(() => {
        const rights = read_cached_rights();

        return {
            rights,
            authorized: Boolean(rights && has_recent_authorization()),
        };
    });
    const [accessMap, setAccessMap] = useState(
        () => build_access_map(initial_access.rights)
    );
    const [access_settings, set_access_settings] = useState(
        () => build_access_settings(initial_access.rights)
    );
    const [loaded, setLoaded] = useState(false);
    const [is_authorized, set_is_authorized] = useState(
        initial_access.authorized ? true : null
    );
    const access_refresh_request_ref = useRef(null);
    const [
        personalization_loaded,
        set_personalization_loaded,
    ] = useState(false);

    const apply_access_rights = useCallback((rights_data) => {
        const normalized_rights = Array.isArray(rights_data)
            ? rights_data
            : [];
        save_cached_rights(normalized_rights);
        setAccessMap(build_access_map(normalized_rights));
        set_access_settings(build_access_settings(normalized_rights));

        return normalized_rights;
    }, []);

    const refresh_access = useCallback(async () => {
        if (access_refresh_request_ref.current) {
            return access_refresh_request_ref.current;
        }

        const request = fetch_access_rights(apiService);
        access_refresh_request_ref.current = request;

        try {
            const rights_data = await request;
            return apply_access_rights(rights_data);

        } finally {
            if (access_refresh_request_ref.current === request) {
                access_refresh_request_ref.current = null;
            }
        }
    }, [apply_access_rights]);

    useEffect(() => {
        let cancelled = false;

        const expire_session = () => {
            clear_cached_access();
            setAccessMap({});
            set_access_settings(build_access_settings([]));
            install_interface_customization_settings(
                DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS
            );
            install_navigation_settings({});
            set_personalization_loaded(true);
            set_is_authorized(false);
            setLoaded(true);
        };

        window.addEventListener('potale:session-expired', expire_session);

        const load_access = async () => {
            try {
                const response = await request_with_retry(
                    () => apiService.check_auth({ timeout: 10000 })
                );

                if (cancelled) {
                    return;
                }

                const authorized = (
                    response?.response === 'Вы авторизованы'
                );
                set_is_authorized(authorized);

                if (authorized && response?.app_theme) {
                    apply_app_theme(response.app_theme);
                }

                if (!authorized) {
                    expire_session();

                    return;
                }

                const cached_rights = (
                    initial_access.authorized
                    && has_recent_authorization(
                        login_access_cache_ms
                    )
                )
                    ? read_cached_rights()
                    : null;
                const rights_request = cached_rights
                    ? Promise.resolve(cached_rights)
                    : fetch_access_rights(apiService);
                const [rights_data] = await Promise.all([
                    rights_request,
                    load_personalization_settings().catch((error) => {
                        console.error(
                            'Ошибка загрузки настроек '
                            + 'интерфейса:',
                            error
                        );
                        install_interface_customization_settings(
                            DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS
                        );
                        install_navigation_settings({});
                    }),
                ]);

                if (!cancelled) {
                    apply_access_rights(rights_data);
                    set_personalization_loaded(true);
                }

            } catch (error) {
                console.error(
                    'Ошибка загрузки прав:',
                    error
                );

                if (!cancelled) {
                    install_interface_customization_settings(
                        DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS
                    );
                    install_navigation_settings({});
                    set_personalization_loaded(true);
                }

                if (!cancelled && !initial_access.authorized) {
                    clear_cached_access();
                    setAccessMap({});
                    set_access_settings(build_access_settings([]));
                    set_is_authorized(false);
                }

            } finally {
                if (!cancelled) {
                    setLoaded(true);
                }
            }
        };

        load_access();

        return () => {
            cancelled = true;
            window.removeEventListener(
                'potale:session-expired', expire_session
            );
        };
    }, [apply_access_rights, initial_access.authorized]);

    useEffect(() => {
        if (is_authorized !== true) {
            return undefined;
        }

        return subscribe_access_realtime({
            apply_access_rights,
            refresh_access,
        });
    }, [
        apply_access_rights,
        is_authorized,
        refresh_access,
    ]);

    const hasAccess = useCallback((code) => {
        if (!code) {
            return true;
        }

        const code_text = String(code).trim();

        if (code_text.includes('.')) {
            return Boolean(accessMap[code_text]);
        }

        const prefix = `${code_text}.`;

        return Boolean(accessMap[code_text]) || Object.keys(accessMap).some(
            (key) => key.startsWith(prefix)
        );
    }, [accessMap]);

    const filterTabs = useCallback(
        (tabs) => (tabs || []).filter((tab) => hasAccess(tab.code)),
        [hasAccess]
    );

    const get_access_setting = useCallback((name, fallback=null) => (
        access_settings?.[name] ?? fallback
    ), [access_settings]);

    return (
        <AccessContext.Provider value={{
            hasAccess,
            filterTabs,
            get_access_setting,
            refresh_access,
            loaded: loaded && personalization_loaded,
            is_authorized,
        }}>
            {children}
        </AccessContext.Provider>
    );
};
